import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import supabase from "../supabaseClient";

// Animated counter component for Stat & Metric Monuments
const AnimatedCounter = ({ value, suffix = "", duration = 1200, start = false }) => {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start) {
      setCount(0);
      return;
    }

    let startTime = null;
    const target = typeof value === "number" ? value : parseInt(value, 10) || 0;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // Ease out quad
      const easedProgress = 1 - (1 - progress) * (1 - progress);
      setCount(Math.floor(easedProgress * target));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [start, value, duration]);

  return (
    <span>
      {count < 10 && typeof value === "number" && value < 10 ? `0${count}` : count}
      {suffix}
    </span>
  );
};

// Budget currency formatter helper
const formatBudget = (amount) => {
  if (!amount) return null;
  const num = typeof amount === "number" ? amount : parseFloat(amount);
  if (isNaN(num)) return null;
  if (num >= 10000000) {
    return `₹${(num / 10000000).toFixed(1)} Cr`;
  }
  if (num >= 100000) {
    return `₹${(num / 100000).toFixed(1)} Lakhs`;
  }
  return `₹${num.toLocaleString("en-IN")}`;
};

// Neoclassical Status Chip Component according to DESIGN.md
const ProjectStatusBadge = ({ status }) => {
  const s = (status || "ongoing").toLowerCase();

  if (s.includes("complete") || s.includes("handover") || s.includes("delivered")) {
    return (
      <span className="px-3 py-1 rounded bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] text-[11px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#059669]"></span>
        Completed &amp; Handover
      </span>
    );
  }

  if (s.includes("ongoing") || s.includes("progress") || s.includes("active") || s.includes("underway")) {
    return (
      <span className="px-3 py-1 rounded bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] text-[11px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#D97706] animate-pulse"></span>
        Ongoing Development
      </span>
    );
  }

  return (
    <span className="px-3 py-1 rounded bg-[#0F172A] border border-slate-700 text-white text-[11px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
      {status || "Govt. Civil Contract"}
    </span>
  );
};

// Default fallback companies data matching the 6 operating entities
const DEFAULT_COMPANIES = [
  {
    id: "bdbd24d8-c918-4459-ab2f-dc052a994b5e",
    name: "Vachana Ventures",
    division: "Real Estate & Construction",
    icon: "holiday_village",
    color: "#2563EB",
    borderTop: "border-t-[#2563EB]",
    short_description:
      "Shaping modern living spaces through quality construction, innovative design, and sustainable development practices in plotted real estate and construction.",
    bullets: [
      "Premium gated plotted developments",
      "Sustainable architectural planning"
    ],
    slug: "vachana-ventures"
  },
  {
    id: "d88bac9b-cd39-4a51-a99f-88f6763cfe23",
    name: "Vachana Construction",
    division: "Civil & Industrial",
    icon: "construction",
    color: "#D97706",
    borderTop: "border-t-[#D97706]",
    short_description:
      "Building excellence across residential, commercial, industrial, and infrastructural developments with an unwavering commitment to quality and engineering precision.",
    bullets: [
      "High-precision commercial complexes",
      "Institutional grade structural civil works"
    ],
    slug: "vachana-construction"
  },
  {
    id: "2c500b4e-c4f3-439f-b884-77673fc6d3e4",
    name: "Vachana Fusion Flats",
    division: "Contemporary Residences",
    icon: "apartment",
    color: "#2563EB",
    borderTop: "border-t-[#2563EB]",
    short_description:
      "Developing modern, stylish, and functional living spaces that blend quality craftsmanship with innovative architectural design for contemporary lifestyles.",
    bullets: [
      "Curated mid-rise urban communities",
      "Smart home enabled living spaces"
    ],
    slug: "vachana-fusion-flats"
  },
  {
    id: "caf7d986-6b2d-4c53-b4f6-33cf59a437d0",
    name: "Vachana Infra",
    division: "Town Planning & Public Infra",
    icon: "alt_route",
    color: "#D97706",
    borderTop: "border-t-[#D97706]",
    short_description:
      "Shaping the future of infrastructure and urban development through turnkey civil construction, town planning, and real estate development with focus on innovation.",
    bullets: [
      "Municipal corridors & bridge works",
      "Public utility networks & drainage systems"
    ],
    slug: "vachana-infra"
  },
  {
    id: "96c4d770-25f4-44e8-b799-0c9b836846ad",
    name: "Vachana Realty",
    division: "Land Acquisition & JV",
    icon: "handshake",
    color: "#2563EB",
    borderTop: "border-t-[#2563EB]",
    short_description:
      "Creating opportunities and building strategic partnerships in real estate through property acquisition, joint ventures, and master-planned infrastructure development.",
    bullets: [
      "Land monetization & legal structuring",
      "Institutional joint-development agreements"
    ],
    slug: "vachana-realty"
  },
  {
    id: "914acd68-d881-42ab-9f79-c109213ed530",
    name: "Vachana Build Works",
    division: "Specialized Contracting",
    icon: "engineering",
    color: "#D97706",
    borderTop: "border-t-[#D97706]",
    short_description:
      "Delivering exceptional construction solutions through strategic collaborations across residential, commercial, and industrial sectors with focus on efficiency.",
    bullets: [
      "Turnkey design-and-build execution",
      "Rapid pre-cast & modular construction"
    ],
    slug: "vachana-build-works"
  }
];

// Fallback real projects from Supabase database
const DEFAULT_PROJECTS = [
  {
    id: "9988da33-3660-4847-aad4-e05c758432a9",
    company_id: "96c4d770-25f4-44e8-b799-0c9b836846ad",
    name: "CHANNABASAVA NAGAR",
    description: "Premium residential plotted development at CHANNABASAVA NAGAR, Mugulnagaon. Designed with master planning, wide avenues, and statutory infrastructure.",
    location: "MUGULNAGAON, Dist. Kalaburagi",
    budget: 1500000,
    status: "ongoing",
    start_date: "2025-01-01",
    projectUrl: "https://res.cloudinary.com/demjpayzu/image/upload/v1755941446/channabasavanagar_pqbi3s.jpg",
    companies: {
      id: "96c4d770-25f4-44e8-b799-0c9b836846ad",
      name: "Vachana Realty",
      slug: "vachana-realty"
    }
  },
  {
    id: "7cb8a349-3c79-47d1-97fb-311495468cd8",
    company_id: "d88bac9b-cd39-4a51-a99f-88f6763cfe23",
    name: "Shanmukh Shivyogi Nagar",
    description: "Landmark civil engineering and residential layout situated prominently at Bangalore Highway, Jewargi, Kalaburagi.",
    location: "Jewargi, Dist. Kalaburagi",
    budget: 1200000,
    status: "ongoing",
    start_date: "2025-01-01",
    projectUrl: "https://res.cloudinary.com/demjpayzu/image/upload/v1755935357/shanmukhnagar_w7hwta.png",
    companies: {
      id: "d88bac9b-cd39-4a51-a99f-88f6763cfe23",
      name: "Vachana Construction",
      slug: "vachana-construction"
    }
  },
  {
    id: "c73c78a5-e29d-4b23-9ff2-bddb1319f1be",
    company_id: "caf7d986-6b2d-4c53-b4f6-33cf59a437d0",
    name: "Anubhava Mega City",
    description: "Integrated mega-township and master-planned residential plotted community located in historic Basavakalyan.",
    location: "Basavakalyan",
    budget: 2000000,
    status: "ongoing",
    start_date: "2025-01-01",
    projectUrl: "https://res.cloudinary.com/demjpayzu/image/upload/v1755935357/megacity_n2lxbr.jpg",
    companies: {
      id: "caf7d986-6b2d-4c53-b4f6-33cf59a437d0",
      name: "Vachana Infra",
      slug: "vachana-infra"
    }
  },
  {
    id: "b6b6d71c-eb9b-4f6f-8386-fb684ed92aed",
    company_id: "bdbd24d8-c918-4459-ab2f-dc052a994b5e",
    name: "SHARANABASAVESHWAR NAGAR",
    description: "Premium residential plotted project at Pala, Kalaburagi featuring secure perimeter, wide internal roads, and environmental amenities.",
    location: "Pala, Dist. Kalaburagi",
    budget: 1500000,
    status: "ongoing",
    start_date: "2025-01-01",
    projectUrl: "https://res.cloudinary.com/demjpayzu/image/upload/v1755935357/sbnagar_ozeacc.jpg",
    companies: {
      id: "bdbd24d8-c918-4459-ab2f-dc052a994b5e",
      name: "Vachana Ventures",
      slug: "vachana-ventures"
    }
  },
  {
    id: "8cfda0c3-fb40-4886-9a36-9010f4261c21",
    company_id: "d88bac9b-cd39-4a51-a99f-88f6763cfe23",
    name: "SHARAN NAGAR",
    description: "High-quality residential plotted community engineered with precision drainage and modern electrical infrastructure in Khanapur.",
    location: "Khanapur",
    budget: 1500000,
    status: "ongoing",
    start_date: "2025-01-01",
    projectUrl: "https://res.cloudinary.com/demjpayzu/image/upload/v1755935356/sharanagar_ayimoa.jpg",
    companies: {
      id: "d88bac9b-cd39-4a51-a99f-88f6763cfe23",
      name: "Vachana Construction",
      slug: "vachana-construction"
    }
  }
];

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companies, setCompanies] = useState(DEFAULT_COMPANIES);
  const [projects, setProjects] = useState(DEFAULT_PROJECTS);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [activeModalProject, setActiveModalProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [statsVisible, setStatsVisible] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    subsidiary: "General Corporate Inquiry",
    message: ""
  });
  const statsRef = useRef(null);

  // Fetch real-time company & projects data from Supabase
  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      try {
        setLoadingProjects(true);

        // 1. Fetch Companies
        const { data: dbCompanies, error: compError } = await supabase
          .from("companies")
          .select("id, name, slug, logo_url, short_description, founded_year, employees_count, img_url");

        if (!compError && dbCompanies && dbCompanies.length > 0 && isMounted) {
          const mergedCompanies = DEFAULT_COMPANIES.map((def) => {
            const match = dbCompanies.find(
              (c) =>
                c.id === def.id ||
                c.name?.toLowerCase().includes(def.name.toLowerCase().replace("vachana ", ""))
            );
            if (match) {
              return {
                ...def,
                id: match.id,
                name: match.name || def.name,
                short_description: match.short_description || def.short_description,
                img_url: match.img_url || null,
                slug: match.slug || def.slug
              };
            }
            return def;
          });
          setCompanies(mergedCompanies);
        }

        // 2. Fetch Projects dynamically from the `projects` table!
        const { data: dbProjects, error: projError } = await supabase
          .from("projects")
          .select("id, company_id, name, description, location, budget, status, start_date, end_date, created_at, projectUrl, companies(id, name, slug)")
          .order("created_at", { ascending: false });

        if (!projError && dbProjects && dbProjects.length > 0 && isMounted) {
          setProjects(dbProjects);
        }
      } catch (err) {
        console.warn("Using fallback cached data:", err);
      } finally {
        if (isMounted) setLoadingProjects(false);
      }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Intersection observer for animating proof counters
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.25 }
    );

    const el = statsRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
      observer.disconnect();
    };
  }, []);

  // Filter projects by category or company
  const filteredProjects = projects.filter((p) => {
    if (selectedFilter === "all") return true;
    if (selectedFilter === "ongoing") return (p.status || "").toLowerCase().includes("ongoing");
    if (selectedFilter === "completed") return (p.status || "").toLowerCase().includes("completed");
    return p.company_id === selectedFilter || p.companies?.name?.toLowerCase().includes(selectedFilter.toLowerCase());
  });

  // Handle direct inquiry on a specific project
  const handleProjectInquiry = (project) => {
    setActiveModalProject(null);
    const targetSubsidiary = project.companies?.name || "General Corporate Inquiry";
    setInquiryForm((prev) => ({
      ...prev,
      subsidiary: targetSubsidiary,
      message: `Inquiry regarding project: ${project.name} (${project.location || "Karnataka"}).`
    }));

    // Smooth scroll to contact section
    const el = document.getElementById("contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleInquirySubmit = (e) => {
    e.preventDefault();
    setInquirySubmitted(true);
    setTimeout(() => {
      setInquiryForm({
        name: "",
        email: "",
        subsidiary: "General Corporate Inquiry",
        message: ""
      });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans-prestige selection:bg-[#2563EB] selection:text-white antialiased">
      {/* =========================================================================
          TOP MASTHEAD & INSTITUTIONAL HEADER
         ========================================================================= */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0] shadow-[0_1px_8px_rgba(15,23,42,0.04)]">
        {/* Top Statutory Bar */}
        <div className="bg-[#0F172A] text-white text-[11px] font-bold tracking-wider uppercase py-2 px-6 lg:px-20 border-b border-slate-800">
          <div className="max-w-[1280px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[15px] text-[#D97706]">mail</span>
                <a className="text-slate-300 hover:text-white transition-colors lowercase" href="mailto:info@vgcompanies.in">
                  info@vgcompanies.in
                </a>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 text-slate-300">
                <span className="material-symbols-outlined text-[15px] text-[#D97706]">location_on</span>
                <span>Karnataka, India</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-slate-400 hidden md:inline">Govt. Class-1 Civil Engineering &amp; Infrastructure</span>
              <span className="text-[#D97706] hidden md:flex items-center gap-1 tracking-widest font-bold">
                <span className="material-symbols-outlined text-[14px]">verified</span>
                ISO 9001:2015
              </span>
            </div>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <div className="h-20 max-w-[1280px] mx-auto px-6 lg:px-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              alt="Vachana Group"
              className="h-11 w-auto object-contain"
              src="/assets/logovg.png"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://lh3.googleusercontent.com/aida/AEtjO1X3dgtAtspBW1x5CksYEXmcfezDQt6YWAScAcr4xheMHrlp4y2Fn_FnPMWTcsLW0KzAwbZz52ngHkzC9VQvtWKIDiWTpWlzF7UTN2-CFcMw2GtKNgS7u4RHHnPXq46AxGG4IJSnF_fMhSP8uWQuOzZnWB9Z-1spqnC3L4EfWsHZnQ_11oR3EbQDv67K6S-Gd_2vPfOK4CjJBmZ4kGO28XwqjvLEOYrXQjvuBzOfdXo";
              }}
            />
            <div className="flex flex-col">
              <span className="font-serif-prestige text-xl font-bold text-[#0F172A] tracking-tight leading-tight group-hover:text-[#2563EB] transition-colors">
                Vachana Group
              </span>
              <span className="font-sans-prestige text-xs text-[#64748B] font-medium tracking-wide">
                ವಚನ ಗ್ರೂಪ್
              </span>
            </div>
          </Link>

          {/* Desktop Nav Items */}
          <nav className="hidden lg:flex items-center gap-6 text-[15px]">
            <Link to="/" className="text-[#2563EB] font-semibold py-1 border-b-2 border-[#2563EB]">
              Home
            </Link>
            <a href="#ecosystem" className="text-[#475569] hover:text-[#0F172A] transition-colors py-1">
              Companies &amp; Subsidiaries
            </a>
            <a href="#projects" className="text-[#475569] hover:text-[#0F172A] transition-colors py-1">
              Signature Projects
            </a>
            <a href="#ethos" className="text-[#475569] hover:text-[#0F172A] transition-colors py-1">
              Why Choose Us
            </a>
            <Link to="/gallery" className="text-[#475569] hover:text-[#0F172A] transition-colors py-1">
              Gallery
            </Link>
            <Link to="/contact" className="text-[#475569] hover:text-[#0F172A] transition-colors py-1">
              Contact
            </Link>
          </nav>

          {/* Actions & Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="#contact"
              className="hidden sm:inline-flex items-center justify-center px-5 py-2.5 rounded bg-[#0F172A] text-white font-sans-prestige text-[13px] font-semibold tracking-wider uppercase border border-slate-800 hover:bg-[#2563EB] hover:border-[#2563EB] shadow-sm transition-all duration-200"
            >
              Inquire Now
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded bg-slate-100 border border-[#E2E8F0] text-[#0F172A]"
              aria-label="Toggle Navigation Menu"
            >
              <span className="material-symbols-outlined text-[24px]">
                {mobileMenuOpen ? "close" : "menu"}
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-[#E2E8F0] px-6 py-4 space-y-3">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block font-semibold text-[#2563EB] py-1"
            >
              Home
            </Link>
            <a
              href="#ecosystem"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-[#475569] py-1"
            >
              Companies &amp; Subsidiaries
            </a>
            <a
              href="#projects"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-[#475569] py-1"
            >
              Signature Projects
            </a>
            <a
              href="#ethos"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-[#475569] py-1"
            >
              Why Choose Us
            </a>
            <Link
              to="/gallery"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-[#475569] py-1"
            >
              Gallery
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-[#475569] py-1"
            >
              Contact
            </Link>
            <a
              href="#contact"
              onClick={() => setMobileMenuOpen(false)}
              className="inline-block w-full text-center py-2.5 rounded bg-[#0F172A] text-white text-[13px] font-semibold tracking-wider uppercase"
            >
              Inquire Now
            </a>
          </div>
        )}
      </header>

      {/* =========================================================================
          HERO SECTION: Monumental Neoclassical Split Layout
         ========================================================================= */}
      <main className="pt-28">
        <section className="relative w-full overflow-hidden bg-gradient-to-b from-[#F1F5F9]/60 via-[#F8FAFC] to-[#F8FAFC] py-16 lg:py-24 border-b border-[#E2E8F0]">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              {/* Left Column: Authoritative Executive Copy */}
              <div className="lg:col-span-7 flex flex-col space-y-6 z-10">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-[#2563EB] text-[11px] font-bold uppercase tracking-wider self-start shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse"></span>
                  <span>Pioneering Infrastructure &amp; Real Estate in Karnataka</span>
                </div>

                <h1 className="font-serif-prestige text-4xl sm:text-5xl lg:text-[54px] font-bold text-[#0F172A] tracking-tight leading-[1.14]">
                  Building Enduring Value Across Modern Living &amp; Infrastructure
                </h1>

                <p className="font-sans-prestige text-lg text-[#475569] max-w-xl leading-relaxed">
                  Vachana Group is a premier multi-disciplinary conglomerate engineering tomorrow's landscapes through excellence across residential housing, commercial complexes, civil infrastructure, and turnkey urban developments.
                </p>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <a
                    href="#ecosystem"
                    className="inline-flex items-center gap-2 px-6 h-12 rounded bg-[#0F172A] text-white font-sans-prestige text-[13px] font-bold tracking-wider uppercase border border-slate-800 hover:bg-[#2563EB] hover:border-[#2563EB] shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <span>Explore Subsidiaries</span>
                    <span className="material-symbols-outlined text-[18px]">arrow_downward</span>
                  </a>
                  <a
                    href="#projects"
                    className="inline-flex items-center gap-2 px-6 h-12 rounded bg-white text-[#0F172A] font-sans-prestige text-[13px] font-semibold tracking-wider uppercase border border-[#E2E8F0] hover:border-[#2563EB] hover:bg-slate-50 shadow-sm transition-all duration-200"
                  >
                    <span className="material-symbols-outlined text-[18px] text-[#2563EB]">domain</span>
                    <span>View DB Projects ({projects.length})</span>
                  </a>
                </div>

                <div className="pt-4 flex flex-wrap items-center gap-6 text-[#64748B] text-[12px] font-bold uppercase tracking-wider">
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[18px] text-[#2563EB]">verified_user</span>
                    <span>Govt. Class-1 Certified</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[18px] text-[#D97706]">workspace_premium</span>
                    <span>ISO 9001:2015 Precision</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[18px] text-[#2563EB]">domain</span>
                    <span>Karnataka Public Works Regd.</span>
                  </div>
                </div>
              </div>

              {/* Right Column: Visual Showcase & Floating Micro-Badge */}
              <div className="lg:col-span-5 relative mt-6 lg:mt-0">
                <div className="relative w-full rounded-lg overflow-hidden card-shadow-level-2 bg-white border border-[#E2E8F0]">
                  <img
                    className="w-full h-[470px] object-cover"
                    alt="Executive architectural vision and civil engineering perspective"
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuAb8INiaUB9PlEa-9wXBA5wJbSPxBnCmGMaXQM6I892cEB0EDKFh-zbEfU6uJZs72PSTkU92rDwGdFrcPLsxrrIIYSOheGhlVjpP2NwRInMLw5Mv9nPgw6Q4KMCSR5b_l9Q2Uy3U-9SilTaaizRh90aCrvX56898iJBR2PoeFsATsQyCZS87JK-_HXiZeAYbv43e7SFLf7LNcgG11QA88Y1N7AoI5R8upML6a4wgA"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/20 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-[#FDE68A]">
                      Integrated Group Leadership
                    </span>
                    <h3 className="font-serif-prestige text-xl sm:text-2xl font-bold text-white mt-1">
                      Monuments of Tomorrow, Engineered Today
                    </h3>
                  </div>
                </div>

                {/* Floating Metric Badge */}
                <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-lg card-shadow-level-2 border border-[#E2E8F0] hidden sm:flex items-center gap-4 z-20">
                  <div className="w-12 h-12 rounded bg-blue-50 border border-blue-100 flex items-center justify-center text-[#2563EB]">
                    <span className="material-symbols-outlined text-[28px]">apartment</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-serif-prestige text-3xl font-bold leading-tight text-[#0F172A]">
                      15+
                    </span>
                    <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
                      Years Proven Delivery
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* =========================================================================
            METRIC MONUMENT BAR: Proof Points
           ========================================================================= */}
        <section className="w-full py-10 bg-white border-b border-[#E2E8F0]" ref={statsRef}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-20">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              
              <div className="p-5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex flex-col justify-between space-y-2 card-shadow-level-1 hover:border-[#2563EB] transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Conglomerate</span>
                  <span className="material-symbols-outlined text-[#2563EB] text-[22px]">corporate_fare</span>
                </div>
                <div className="font-serif-prestige text-4xl sm:text-5xl font-bold text-[#0F172A] tracking-tight">
                  <AnimatedCounter value={companies.length || 6} start={statsVisible} />
                </div>
                <p className="text-[13px] text-[#475569] font-medium">Specialized Operating Subsidiaries</p>
              </div>

              <div className="p-5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex flex-col justify-between space-y-2 card-shadow-level-1 hover:border-[#2563EB] transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Excellence</span>
                  <span className="material-symbols-outlined text-[#D97706] text-[22px]">history_edu</span>
                </div>
                <div className="font-serif-prestige text-4xl sm:text-5xl font-bold text-[#0F172A] tracking-tight">
                  <AnimatedCounter value={15} suffix="+" start={statsVisible} />
                </div>
                <p className="text-[13px] text-[#475569] font-medium">Years of Structural Mastery</p>
              </div>

              {/* Dynamic Projects Count from Database */}
              <div className="p-5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex flex-col justify-between space-y-2 card-shadow-level-1 hover:border-[#2563EB] transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Database Projects</span>
                  <span className="material-symbols-outlined text-[#2563EB] text-[22px]">domain</span>
                </div>
                <div className="font-serif-prestige text-4xl sm:text-5xl font-bold text-[#0F172A] tracking-tight">
                  <AnimatedCounter value={projects.length || 5} suffix="+" start={statsVisible} />
                </div>
                <p className="text-[13px] text-[#475569] font-medium">Active &amp; Delivered Developments</p>
              </div>

              <div className="p-5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex flex-col justify-between space-y-2 card-shadow-level-1 hover:border-[#2563EB] transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-widest">Patrons</span>
                  <span className="material-symbols-outlined text-[#2563EB] text-[22px]">groups</span>
                </div>
                <div className="font-serif-prestige text-4xl sm:text-5xl font-bold text-[#0F172A] tracking-tight">
                  <AnimatedCounter value={4} suffix="K+" start={statsVisible} />
                </div>
                <p className="text-[13px] text-[#475569] font-medium">Satisfied Homeowners &amp; Partners</p>
              </div>

            </div>
          </div>
        </section>

        {/* =========================================================================
            THE 6 COMPANIES ECOSYSTEM (Enterprise Portfolio Grid)
           ========================================================================= */}
        <section className="w-full py-20 bg-[#F8FAFC]" id="ecosystem">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-20">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
              <div className="space-y-2 max-w-2xl">
                <span className="text-[11px] font-bold text-[#2563EB] uppercase tracking-widest">
                  Enterprise Portfolio
                </span>
                <h2 className="font-serif-prestige text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
                  A Diversified Conglomerate of Specialized Ventures
                </h2>
                <p className="text-[15px] text-[#475569] leading-relaxed">
                  Discover our integrated ecosystem spanning real estate, civil engineering, smart urban infrastructure, and contemporary living spaces across Karnataka.
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-[#64748B]">
                <span className="text-[#0F172A]">{companies.length} Active Entities</span>
                <span className="text-[#CBD5E1]">/</span>
                <span>Turnkey Integration</span>
              </div>
            </div>

            {/* 3-Column Subsidiary Cards with 3px Accent Stripes from DESIGN.md */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {companies.map((company, index) => {
                const targetLink = `/company/${company.id}`;

                return (
                  <div
                    key={company.id || index}
                    className={`group relative flex flex-col justify-between p-7 rounded-lg bg-white border border-[#E2E8F0] border-t-[3px] ${company.borderTop || "border-t-[#2563EB]"} card-shadow-level-1 hover:card-shadow-level-2 hover:-translate-y-1 transition-all duration-300`}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="px-2.5 py-1 rounded bg-slate-100 text-slate-700 text-[11px] font-bold uppercase tracking-wider border border-slate-200">
                          {company.division || "Enterprise Venture"}
                        </span>
                        <div className="w-10 h-10 rounded bg-slate-50 border border-slate-200 flex items-center justify-center text-[#0F172A] group-hover:bg-[#0F172A] group-hover:text-white transition-colors">
                          <span className="material-symbols-outlined text-[20px]">{company.icon || "corporate_fare"}</span>
                        </div>
                      </div>

                      <div>
                        <h3 className="font-serif-prestige text-2xl font-bold text-[#0F172A] group-hover:text-[#2563EB] transition-colors">
                          {company.name}
                        </h3>
                        <p className="text-[14px] text-[#475569] mt-2 leading-relaxed">
                          {company.short_description}
                        </p>
                      </div>

                      {company.bullets && (
                        <ul className="space-y-1.5 text-[13px] text-[#475569] pt-2 border-t border-slate-100">
                          {company.bullets.map((b, bIdx) => (
                            <li key={bIdx} className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-[16px] text-[#2563EB]">check_circle</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="pt-6 mt-6 border-t border-slate-100">
                      <Link
                        to={targetLink}
                        className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#0F172A] group-hover:text-[#2563EB] transition-colors"
                      >
                        <span>Explore Venture &amp; Projects</span>
                        <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">
                          arrow_forward
                        </span>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            WHY CHOOSE OUR GROUP: Neoclassical Pillars & Guiding Ethos
           ========================================================================= */}
        <section className="w-full py-20 bg-slate-50 border-t border-b border-[#E2E8F0]" id="ethos">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              <div className="lg:col-span-5 space-y-6">
                <span className="text-[11px] font-bold text-[#2563EB] uppercase tracking-widest">
                  Our Guiding Ethos
                </span>
                <h2 className="font-serif-prestige text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
                  Engineered on Integrity, Built for the Future
                </h2>
                <p className="text-[15px] text-[#475569] leading-relaxed">
                  From our inception in Karnataka, Vachana Group has remained anchored to an uncompromising code of craftsmanship, fiduciary discipline, and enduring community impact.
                </p>

                {/* Milestone Delivery Tracker */}
                <div className="p-5 rounded-lg bg-white border border-[#E2E8F0] card-shadow-level-1 space-y-3">
                  <div className="flex items-center justify-between text-[11px] font-bold text-[#475569] uppercase">
                    <span>Project Delivery On-Time Rate</span>
                    <span className="text-[#0F172A] font-bold text-sm">98.4%</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#2563EB] rounded-full" style={{ width: "98.4%" }}></div>
                  </div>
                  <div className="flex items-center justify-between text-[13px] text-[#64748B] pt-1">
                    <span>15-Year Metric Tracking</span>
                    <span className="text-[#2563EB] font-semibold">Verified Class-1 Standard</span>
                  </div>
                </div>
              </div>

              {/* 4 Pillars Grid */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                
                <div className="p-5 rounded-lg bg-white border border-[#E2E8F0] card-shadow-level-1 space-y-2 hover:border-[#2563EB] transition-colors">
                  <div className="w-10 h-10 rounded bg-blue-50 border border-blue-100 flex items-center justify-center text-[#2563EB]">
                    <span className="material-symbols-outlined text-[24px]">verified</span>
                  </div>
                  <h4 className="font-sans-prestige text-[17px] font-bold text-[#0F172A]">Trust First</h4>
                  <p className="text-[13px] text-[#475569] leading-relaxed">
                    Deep ethical commitment, strict statutory compliance, and transparent governance built over 15+ years of steadfast client service.
                  </p>
                </div>

                <div className="p-5 rounded-lg bg-white border border-[#E2E8F0] card-shadow-level-1 space-y-2 hover:border-[#2563EB] transition-colors">
                  <div className="w-10 h-10 rounded bg-blue-50 border border-blue-100 flex items-center justify-center text-[#2563EB]">
                    <span className="material-symbols-outlined text-[24px]">hub</span>
                  </div>
                  <h4 className="font-sans-prestige text-[17px] font-bold text-[#0F172A]">Collaborative Synergy</h4>
                  <p className="text-[13px] text-[#475569] leading-relaxed">
                    Seamless cross-company coordination allows turnkey execution from raw land acquisition to architectural handover under one standard.
                  </p>
                </div>

                <div className="p-5 rounded-lg bg-white border border-[#E2E8F0] card-shadow-level-1 space-y-2 hover:border-[#2563EB] transition-colors">
                  <div className="w-10 h-10 rounded bg-blue-50 border border-blue-100 flex items-center justify-center text-[#2563EB]">
                    <span className="material-symbols-outlined text-[24px]">troubleshoot</span>
                  </div>
                  <h4 className="font-sans-prestige text-[17px] font-bold text-[#0F172A]">Results-Driven Execution</h4>
                  <p className="text-[13px] text-[#475569] leading-relaxed">
                    Punctual delivery milestones, superior grade structural engineering, and verified quality benchmarks monitored at every concrete pour.
                  </p>
                </div>

                <div className="p-5 rounded-lg bg-white border border-[#E2E8F0] card-shadow-level-1 space-y-2 hover:border-[#2563EB] transition-colors">
                  <div className="w-10 h-10 rounded bg-amber-50 border border-amber-100 flex items-center justify-center text-[#D97706]">
                    <span className="material-symbols-outlined text-[24px]">eco</span>
                  </div>
                  <h4 className="font-sans-prestige text-[17px] font-bold text-[#0F172A]">Sustainable Innovation</h4>
                  <p className="text-[13px] text-[#475569] leading-relaxed">
                    Eco-efficient construction methodologies, green materials, and community-first master planning for climate-resilient futures.
                  </p>
                </div>

              </div>

            </div>
          </div>
        </section>

        {/* =========================================================================
            SIGNATURE DEVELOPMENTS: Dynamic Database Projects Section
           ========================================================================= */}
        <section className="w-full py-20 bg-[#F8FAFC]" id="projects">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-20">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-[#2563EB] text-[11px] font-bold uppercase tracking-wider">
                  <span className="w-2 h-2 rounded-full bg-[#2563EB]"></span>
                  <span>Live Database Portfolio</span>
                </div>
                <h2 className="font-serif-prestige text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
                  Signature Developments
                </h2>
                <p className="text-[15px] text-[#475569]">
                  Explore current master-planned projects, gated residential communities, and civil works across Karnataka.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wider text-[#0F172A] hover:text-[#2563EB] transition-colors"
                >
                  <span>Inquire on Current Availability</span>
                  <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                </a>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-2 pb-8 overflow-x-auto no-scrollbar border-b border-[#E2E8F0] mb-10">
              <button
                onClick={() => setSelectedFilter("all")}
                className={`px-4 py-2 rounded text-[13px] font-bold tracking-wider uppercase transition-all shrink-0 ${
                  selectedFilter === "all"
                    ? "bg-[#0F172A] text-white shadow-sm"
                    : "bg-white text-[#475569] border border-[#E2E8F0] hover:bg-slate-50"
                }`}
              >
                All Projects ({projects.length})
              </button>
              <button
                onClick={() => setSelectedFilter("ongoing")}
                className={`px-4 py-2 rounded text-[13px] font-bold tracking-wider uppercase transition-all shrink-0 ${
                  selectedFilter === "ongoing"
                    ? "bg-[#0F172A] text-white shadow-sm"
                    : "bg-white text-[#475569] border border-[#E2E8F0] hover:bg-slate-50"
                }`}
              >
                Ongoing Projects
              </button>
              {companies.map((comp) => (
                <button
                  key={comp.id}
                  onClick={() => setSelectedFilter(comp.id)}
                  className={`px-4 py-2 rounded text-[13px] font-bold tracking-wider uppercase transition-all shrink-0 ${
                    selectedFilter === comp.id
                      ? "bg-[#0F172A] text-white shadow-sm"
                      : "bg-white text-[#475569] border border-[#E2E8F0] hover:bg-slate-50"
                  }`}
                >
                  {comp.name.replace("Vachana ", "")}
                </button>
              ))}
            </div>

            {/* Dynamic Project Cards Grid */}
            {loadingProjects && projects.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="rounded-lg overflow-hidden bg-white border border-[#E2E8F0] p-6 space-y-4 animate-pulse">
                    <div className="h-56 bg-slate-200 rounded"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                    <div className="h-6 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-12 bg-slate-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-lg border border-[#E2E8F0] p-8">
                <span className="material-symbols-outlined text-[48px] text-[#64748B]">search_off</span>
                <h3 className="font-serif-prestige text-xl font-bold text-[#0F172A] mt-2">No projects found</h3>
                <p className="text-[14px] text-[#475569] mt-1">There are no projects matching this filter right now.</p>
                <button
                  onClick={() => setSelectedFilter("all")}
                  className="mt-4 px-4 py-2 rounded bg-[#0F172A] text-white text-[12px] font-bold uppercase tracking-wider"
                >
                  View All Projects
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project) => {
                  const companyName = project.companies?.name || "Vachana Group";
                  const formattedBudget = formatBudget(project.budget);
                  const imageUrl =
                    project.projectUrl ||
                    "https://res.cloudinary.com/demjpayzu/image/upload/v1755941446/channabasavanagar_pqbi3s.jpg";

                  return (
                    <div
                      key={project.id}
                      className="group rounded-lg overflow-hidden bg-white border border-[#E2E8F0] card-shadow-level-1 hover:card-shadow-level-2 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
                    >
                      <div>
                        {/* Project Card Image & Status Badge */}
                        <div className="relative h-64 overflow-hidden bg-slate-100">
                          <img
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            alt={project.name}
                            src={imageUrl}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://lh3.googleusercontent.com/aida-public/AB6AXuDP-eHgiIQUBPfNAskFtGAY1GHlFLFGS4qqBX2bUXC9001fGk0SX1a3R1PRM6RpGMNOslg3DUQASyvIgfI5abZfS67-zFRzoHX6bPEZwloEkhWiFG6VRJOuArvCMeaGap-IMXlTCdxM9RKzsM6T5x_rm8NbpWo38nktCUmprXR8G6vDQ3PmT2dsbjx3Qa0WO0xsLVAdyhouoE6sycHzIlX-rZf24ZwqPv22Ypx0FQ";
                            }}
                          />
                          <div className="absolute top-4 left-4 z-10">
                            <ProjectStatusBadge status={project.status} />
                          </div>
                          {formattedBudget && (
                            <div className="absolute bottom-4 right-4 z-10 bg-[#0F172A]/90 backdrop-blur-md px-3 py-1 rounded text-white text-[12px] font-bold">
                              Budget: {formattedBudget}
                            </div>
                          )}
                        </div>

                        {/* Project Details */}
                        <div className="p-6 space-y-3">
                          <div className="flex items-center justify-between text-[12px] text-[#64748B] font-semibold">
                            <span className="text-[#2563EB] font-bold uppercase tracking-wider">{companyName}</span>
                            {project.location && (
                              <span className="flex items-center gap-1 text-slate-500">
                                <span className="material-symbols-outlined text-[14px]">location_on</span>
                                {project.location}
                              </span>
                            )}
                          </div>

                          <h4 className="font-serif-prestige text-xl font-bold text-[#0F172A] group-hover:text-[#2563EB] transition-colors leading-snug">
                            {project.name}
                          </h4>

                          <p className="text-[13px] text-[#475569] leading-relaxed line-clamp-3">
                            {project.description || "Master-planned civil development engineered to the highest statutory standards across Karnataka."}
                          </p>
                        </div>
                      </div>

                      {/* Project Card Footer */}
                      <div className="p-6 pt-0 flex items-center justify-between border-t border-slate-100 mt-4">
                        <button
                          onClick={() => setActiveModalProject(project)}
                          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#0F172A] group-hover:text-[#2563EB] transition-colors"
                        >
                          <span>View Blueprint Details</span>
                          <span className="material-symbols-outlined text-[16px]">visibility</span>
                        </button>
                        <button
                          onClick={() => handleProjectInquiry(project)}
                          className="px-3.5 py-1.5 rounded bg-slate-100 hover:bg-[#2563EB] hover:text-white text-[#0F172A] text-[12px] font-bold uppercase tracking-wider transition-colors"
                        >
                          Inquire
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* =========================================================================
            PROJECT BLUEPRINT MODAL
           ========================================================================= */}
        {activeModalProject && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setActiveModalProject(null)}
          >
            <div
              className="bg-white rounded-lg card-shadow-level-2 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#E2E8F0]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header Image */}
              <div className="relative h-64 sm:h-72 w-full bg-slate-100">
                <img
                  className="w-full h-full object-cover"
                  src={
                    activeModalProject.projectUrl ||
                    "https://res.cloudinary.com/demjpayzu/image/upload/v1755941446/channabasavanagar_pqbi3s.jpg"
                  }
                  alt={activeModalProject.name}
                />
                <button
                  onClick={() => setActiveModalProject(null)}
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 text-[#0F172A] flex items-center justify-center hover:bg-white shadow-md transition-colors"
                  aria-label="Close modal"
                >
                  <span className="material-symbols-outlined text-[20px]">close</span>
                </button>
                <div className="absolute top-4 left-4">
                  <ProjectStatusBadge status={activeModalProject.status} />
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 sm:p-8 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[12px] font-bold uppercase tracking-wider text-[#2563EB]">
                    {activeModalProject.companies?.name || "Vachana Group"}
                  </span>
                  {activeModalProject.start_date && (
                    <span className="text-[12px] text-[#64748B]">
                      Commenced: {new Date(activeModalProject.start_date).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>

                <h3 className="font-serif-prestige text-2xl sm:text-3xl font-bold text-[#0F172A]">
                  {activeModalProject.name}
                </h3>

                {/* Key specs pill row */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                  <div className="p-3 rounded bg-slate-50 border border-slate-200">
                    <span className="block text-[11px] font-bold text-slate-500 uppercase">Location</span>
                    <span className="font-semibold text-[13px] text-[#0F172A]">{activeModalProject.location || "Karnataka"}</span>
                  </div>
                  {activeModalProject.budget && (
                    <div className="p-3 rounded bg-slate-50 border border-slate-200">
                      <span className="block text-[11px] font-bold text-slate-500 uppercase">Project Outlay</span>
                      <span className="font-semibold text-[13px] text-[#0F172A]">{formatBudget(activeModalProject.budget)}</span>
                    </div>
                  )}
                  <div className="p-3 rounded bg-slate-50 border border-slate-200">
                    <span className="block text-[11px] font-bold text-slate-500 uppercase">Execution</span>
                    <span className="font-semibold text-[13px] text-[#0F172A]">Class-1 Standard</span>
                  </div>
                </div>

                <div className="pt-2">
                  <h4 className="text-[12px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                    Project Overview
                  </h4>
                  <p className="text-[14px] text-[#475569] leading-relaxed whitespace-pre-line">
                    {activeModalProject.description || "Master-planned infrastructure development incorporating architectural integrity and sustainable engineering."}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="pt-6 flex flex-col sm:flex-row gap-3 border-t border-slate-100">
                  <button
                    onClick={() => handleProjectInquiry(activeModalProject)}
                    className="flex-1 h-12 rounded bg-[#0F172A] text-white text-[13px] font-bold uppercase tracking-wider hover:bg-[#2563EB] transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Inquire About This Project</span>
                    <span className="material-symbols-outlined text-[18px]">send</span>
                  </button>
                  {activeModalProject.company_id && (
                    <Link
                      to={`/company/${activeModalProject.company_id}`}
                      className="h-12 px-5 rounded border border-[#E2E8F0] hover:border-[#2563EB] text-[#0F172A] text-[13px] font-semibold tracking-wider uppercase flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <span>Explore Company</span>
                      <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
            PARTNER WITH VACHANA GROUP: Direct Desk & Quick Inquiry
           ========================================================================= */}
        <section className="w-full py-20 bg-[#0F172A] text-white relative overflow-hidden" id="contact">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-20 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              {/* Left Column: Direct Institutional Desk */}
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-[#FDE68A] text-[11px] font-bold uppercase tracking-wider">
                  <span className="material-symbols-outlined text-[15px]">corporate_fare</span>
                  <span>Direct Institutional Desk</span>
                </div>
                <h2 className="font-serif-prestige text-3xl sm:text-5xl font-bold text-white tracking-tight leading-tight">
                  Partner with Vachana Group
                </h2>
                <p className="text-lg text-slate-300 max-w-xl leading-relaxed">
                  Whether you are a land owner exploring joint ventures, an institutional investor seeking infrastructure opportunities, or a prospective homebuyer, our leadership is ready to connect.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <a
                    href="mailto:info@vgcompanies.in"
                    className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded bg-white text-[#0F172A] text-[13px] font-bold tracking-wider uppercase shadow-md hover:bg-slate-100 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px] text-[#2563EB]">mail</span>
                    <span>info@vgcompanies.in</span>
                  </a>
                  <a
                    href="tel:+918000000000"
                    className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded bg-slate-800 text-white text-[13px] font-semibold tracking-wider uppercase border border-slate-700 hover:bg-slate-700 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px] text-[#D97706]">call</span>
                    <span>Schedule Executive Call</span>
                  </a>
                </div>
              </div>

              {/* Right Column: Architectural Quick Inquiry Card */}
              <div className="lg:col-span-5 bg-white text-[#0F172A] p-7 rounded-lg card-shadow-level-2 border border-[#E2E8F0]">
                <h3 className="font-serif-prestige text-2xl font-bold text-[#0F172A] mb-1">
                  Initiate an Inquiry
                </h3>
                <p className="text-[13px] text-[#475569] mb-5">
                  Our corporate relations desk responds to all formal project and partnership requests within 24 business hours.
                </p>

                {inquirySubmitted ? (
                  <div className="p-4 rounded-lg bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] text-center space-y-2">
                    <span className="material-symbols-outlined text-[32px] text-[#059669]">check_circle</span>
                    <p className="font-bold text-[15px]">Inquiry Received</p>
                    <p className="text-[13px]">
                      Thank you. Our corporate desk has received your details and will contact you promptly.
                    </p>
                    <button
                      onClick={() => setInquirySubmitted(false)}
                      className="mt-2 text-[12px] underline font-semibold text-[#065F46]"
                    >
                      Submit another inquiry
                    </button>
                  </div>
                ) : (
                  <form className="space-y-3" onSubmit={handleInquirySubmit}>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-[#64748B] mb-1">
                        Full Name / Organization
                      </label>
                      <input
                        className="w-full px-3.5 py-2.5 rounded bg-white border border-[#CBD5E1] text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 transition-all text-sm"
                        placeholder="e.g. Ramesh Patil / Patil Holdings"
                        value={inquiryForm.name}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                        required
                        type="text"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-[#64748B] mb-1">
                        Official Email Address
                      </label>
                      <input
                        className="w-full px-3.5 py-2.5 rounded bg-white border border-[#CBD5E1] text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 transition-all text-sm"
                        placeholder="name@company.com"
                        value={inquiryForm.email}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                        required
                        type="email"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-[#64748B] mb-1">
                        Subsidiary / Area of Interest
                      </label>
                      <select
                        className="w-full px-3.5 py-2.5 rounded bg-white border border-[#CBD5E1] text-[#0F172A] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 transition-all text-sm"
                        value={inquiryForm.subsidiary}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, subsidiary: e.target.value })}
                      >
                        <option>General Corporate Inquiry</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-[#64748B] mb-1">
                        Message / Project Brief
                      </label>
                      <textarea
                        rows="2"
                        className="w-full px-3.5 py-2.5 rounded bg-white border border-[#CBD5E1] text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 transition-all text-sm resize-none"
                        placeholder="Brief outline of your inquiry or requirements..."
                        value={inquiryForm.message}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                      ></textarea>
                    </div>
                    <button
                      className="w-full h-11 mt-1 rounded bg-[#0F172A] text-white text-[13px] font-bold tracking-wider uppercase border border-slate-800 shadow-md hover:bg-[#2563EB] hover:border-[#2563EB] transition-all duration-200"
                      type="submit"
                    >
                      Submit Corporate Inquiry
                    </button>
                  </form>
                )}
              </div>

            </div>
          </div>
        </section>
      </main>

      {/* =========================================================================
          INSTITUTIONAL FOOTER
         ========================================================================= */}
      <footer className="w-full bg-[#0B132B] text-white border-t border-slate-800">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-20 pt-16 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 pb-14 border-b border-slate-800">
            
            {/* Brand Heritage */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-3">
                <img
                  alt="Vachana Group"
                  className="h-10 w-auto object-contain bg-white p-1 rounded"
                  src="/assets/logovg.png"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://lh3.googleusercontent.com/aida/AEtjO1X3dgtAtspBW1x5CksYEXmcfezDQt6YWAScAcr4xheMHrlp4y2Fn_FnPMWTcsLW0KzAwbZz52ngHkzC9VQvtWKIDiWTpWlzF7UTN2-CFcMw2GtKNgS7u4RHHnPXq46AxGG4IJSnF_fMhSP8uWQuOzZnWB9Z-1spqnC3L4EfWsHZnQ_11oR3EbQDv67K6S-Gd_2vPfOK4CjJBmZ4kGO28XwqjvLEOYrXQjvuBzOfdXo";
                  }}
                />
                <div className="flex flex-col">
                  <span className="font-serif-prestige text-xl font-bold text-white tracking-tight">
                    Vachana Group
                  </span>
                  <span className="font-sans-prestige text-xs text-[#FDE68A] font-medium">
                    ವಚನ ಸಮೂಹ ಸಂಸ್ಥೆಗಳು
                  </span>
                </div>
              </div>
              <p className="text-[14px] text-slate-300 max-w-md leading-relaxed">
                Pioneering landmark civil infrastructure, premium residential environments, and high-precision commercial engineering across Karnataka. Grounded in integrity, permanence, and forward-looking architectural design.
              </p>
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center gap-1.5 text-slate-300 text-[11px] font-bold uppercase tracking-wider">
                  <span className="material-symbols-outlined text-[#2563EB] text-[18px]">verified</span>
                  <span>Govt. Registered Class-1 Contractor</span>
                </div>
              </div>
            </div>

            {/* The 6 Companies */}
            <div>
              <h4 className="text-[14px] font-bold text-white uppercase tracking-wider mb-4">
                The 6 Companies
              </h4>
              <ul className="space-y-2 text-[13px] text-slate-300">
                {companies.map((c) => (
                  <li key={c.id}>
                    <Link className="hover:text-white transition-colors block" to={`/company/${c.id}`}>
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-[14px] font-bold text-white uppercase tracking-wider mb-4">
                Quick Links
              </h4>
              <ul className="space-y-2 text-[13px] text-slate-300">
                <li><a className="hover:text-white transition-colors block" href="#ethos">About Us</a></li>
                <li><a className="hover:text-white transition-colors block" href="#projects">Signature Projects</a></li>
                <li><a className="hover:text-white transition-colors block" href="#ethos">Why Choose Us</a></li>
                <li><Link className="hover:text-white transition-colors block" to="/gallery">Media &amp; Gallery</Link></li>
                <li><Link className="hover:text-white transition-colors block" to="/contact">Contact Corporate Desk</Link></li>
              </ul>
            </div>

            {/* Head Office */}
            <div>
              <h4 className="text-[14px] font-bold text-white uppercase tracking-wider mb-4">
                Head Office
              </h4>
              <div className="space-y-2 text-[13px] text-slate-300">
                <p className="leading-relaxed">
                  Vachana Group Corporate Towers,<br />
                  Karnataka, India
                </p>
                <p className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px] text-[#FDE68A]">mail</span>
                  <a className="hover:text-white transition-colors" href="mailto:info@vgcompanies.in">
                    info@vgcompanies.in
                  </a>
                </p>
                <p className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px] text-[#FDE68A]">schedule</span>
                  <span>Mon - Sat: 9:00 AM - 6:30 PM</span>
                </p>
              </div>
            </div>

          </div>

          {/* Footer Bottom Bar */}
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-slate-400 text-[12px]">
            <p>© {new Date().getFullYear()} Vachana Group. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a className="hover:text-white transition-colors" href="#contact">Privacy Policy</a>
              <a className="hover:text-white transition-colors" href="#contact">Terms of Service</a>
              <a className="hover:text-white transition-colors" href="#ecosystem">Sitemap</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
